import { Promotion } from "./types"

/**
 * Normalize a name for duplicate matching: lowercase and strip punctuation/whitespace, so
 * "Bozzuto's" and "Bozzutos" (or "March TPR" and "march-tpr") compare equal.
 */
export function normalize_for_match(value: string | undefined): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "")
}

/** Inclusive overlap of two YYYY-MM-DD ranges; false when either range is entirely missing. */
export function promotion_date_ranges_overlap(
  a: Pick<Promotion, "start_date" | "end_date">,
  b: Pick<Promotion, "start_date" | "end_date">,
): boolean {
  const a_start = a.start_date || a.end_date
  const a_end = a.end_date || a.start_date
  const b_start = b.start_date || b.end_date
  const b_end = b.end_date || b.start_date
  if (!a_start || !a_end || !b_start || !b_end) return false
  // YYYY-MM-DD strings compare correctly lexicographically.
  return a_start <= b_end && b_start <= a_end
}

function names_similar(a: string | undefined, b: string | undefined): boolean {
  const na = normalize_for_match(a)
  const nb = normalize_for_match(b)
  if (na.length < 4 || nb.length < 4) return na.length > 0 && na === nb
  return na === nb || na.includes(nb) || nb.includes(na)
}

export interface DuplicateCandidate {
  name: string
  customer?: string
  promotion_type?: string
  start_date?: string
  end_date?: string
}

/**
 * Existing promotions that look like the one being created. Flags when the customer matches
 * (punctuation-insensitive) and either the names are similar, or the date ranges overlap with
 * the same promotion type. Non-blocking — the UI shows these as a warning, not an error.
 */
export function find_potential_duplicate_promotions(
  candidate: DuplicateCandidate,
  existing: Promotion[],
  limit = 5,
): Promotion[] {
  const candidate_customer = normalize_for_match(candidate.customer)
  if (!candidate_customer) return []

  const matches = existing.filter(promotion => {
    if (normalize_for_match(promotion.customer) !== candidate_customer) return false
    if (names_similar(candidate.name, promotion.name)) return true
    const candidate_type = normalize_for_match(candidate.promotion_type)
    const same_type = candidate_type.length > 0 && candidate_type === normalize_for_match(promotion.promotion_type)
    return same_type && promotion_date_ranges_overlap(candidate, promotion)
  })
  return matches.slice(0, limit)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function promo(overrides: Partial<Promotion>): Promotion {
    return { name: "Promo", products: [], ...overrides }
  }

  describe("normalize_for_match", () => {
    it("ignores punctuation and case", () => {
      expect(normalize_for_match("Bozzuto's")).toBe(normalize_for_match("bozzutos"))
      expect(normalize_for_match("March TPR - 2026")).toBe("marchtpr2026")
      expect(normalize_for_match(undefined)).toBe("")
    })
  })

  describe("promotion_date_ranges_overlap", () => {
    it("detects overlap and non-overlap", () => {
      const a = { start_date: "2026-03-01", end_date: "2026-03-31" }
      expect(promotion_date_ranges_overlap(a, { start_date: "2026-03-15", end_date: "2026-04-15" })).toBe(true)
      expect(promotion_date_ranges_overlap(a, { start_date: "2026-04-01", end_date: "2026-04-30" })).toBe(false)
    })
    it("uses the single available date when one side of a range is missing", () => {
      expect(
        promotion_date_ranges_overlap({ start_date: "2026-03-10" }, { start_date: "2026-03-01", end_date: "2026-03-31" })
      ).toBe(true)
    })
    it("returns false when a range is entirely missing", () => {
      expect(promotion_date_ranges_overlap({}, { start_date: "2026-03-01", end_date: "2026-03-31" })).toBe(false)
    })
  })

  describe("find_potential_duplicate_promotions", () => {
    const existing = [
      promo({ id: "p1", name: "March Display Program", customer: "Bozzuto's", promotion_type: "Display", start_date: "2026-03-01", end_date: "2026-03-31" }),
      promo({ id: "p2", name: "Summer TPR", customer: "KeHE", promotion_type: "TPR", start_date: "2026-06-01", end_date: "2026-06-30" }),
      promo({ id: "p3", name: "Fall Scan", customer: "Bozzuto's", promotion_type: "Scan", start_date: "2026-09-01", end_date: "2026-09-30" }),
    ]

    it("flags same-customer promotions with similar names, across spelling variants", () => {
      const found = find_potential_duplicate_promotions(
        { name: "march display program", customer: "Bozzutos" },
        existing,
      )
      expect(found.map(p => p.id)).toEqual(["p1"])
    })

    it("flags same customer + same type + overlapping dates even with a different name", () => {
      const found = find_potential_duplicate_promotions(
        { name: "New spring push", customer: "Bozzuto's", promotion_type: "Display", start_date: "2026-03-15", end_date: "2026-04-15" },
        existing,
      )
      expect(found.map(p => p.id)).toEqual(["p1"])
    })

    it("does not flag different customers or non-overlapping different names", () => {
      expect(
        find_potential_duplicate_promotions({ name: "March Display Program", customer: "UNFI" }, existing)
      ).toEqual([])
      expect(
        find_potential_duplicate_promotions(
          { name: "Winter promo", customer: "Bozzuto's", promotion_type: "Display", start_date: "2026-12-01", end_date: "2026-12-31" },
          existing,
        )
      ).toEqual([])
    })

    it("returns nothing without a customer", () => {
      expect(find_potential_duplicate_promotions({ name: "March Display Program" }, existing)).toEqual([])
    })
  })
}
